import React, { useEffect, useState } from 'react';
import { path, propEq, find, omit, prop, pick } from 'ramda';
import { branch, compose, renderNothing, withProps, renderComponent, mapProps } from 'recompose';
import { useNavigate } from 'js/hooks/navigate/useNavigate';
import { usePortletConfig, withForLoggedIn, withPortlet } from 'js/components/system/Portlet';
import { useAppSelector, useUserSelector } from 'js/store/hooks';
import { componentService } from 'js/services/component.service';
import { DISPLAY_PLAYER, MYSTERY_PARCEL_PAGE } from './constants';
import { statuses } from '../constants';
import { withEmptyState } from '../common/EmptyState';
import withLeaderBoardDetails from './withDetails';
import Details from './Details';
import Skeleton from './Skeleton';
import { withTranslations } from './DetailedPage/hooks/useTranslate';
import DetailedPage, { extractMysteryParcelId } from './DetailedPage';
import {
    withDynamicReducer,
    useDynamicReducerDispatch,
    useDynamicReducerSelector,
    updateJoinState
} from './DetailedPage/state';
import withSetNicknameOnce from '../Nickname/withSetNicknameOnce';

export const NavigateHome = () => {
    const navigateWithReplace = useNavigate({ replace: true });
    const rootPage = useAppSelector(prop('rootPage'));

    useEffect(() => {
        navigateWithReplace(rootPage);
    }, [rootPage, navigateWithReplace]);

    return null;
};

export const withLeaderBoardId = withProps(
    () => {
        // save pathname for case if page in popup was opened over LB details page
        const [pathname] = useState(window.location.pathname);
        const leaderBoardPage = 'pass-the-parcel';
        const pathArray = pathname.split('/').filter(Boolean);
        let leaderBoardPageIndex = -1;
        let leaderboardId = null;

        pathArray.forEach((part, idx) => {
            if (part === leaderBoardPage) {
                leaderBoardPageIndex = idx;
            }

            if (leaderBoardPageIndex > -1 && idx === leaderBoardPageIndex + 1) {
                leaderboardId = part;
            }
        });

        return {
            isNotLeaderBoardPage: leaderBoardPageIndex < 0,
            leaderboardId
        };
    }
);

export const onlyForPlayers = compose(
    withProps(() => useUserSelector(pick(['role']))),
    branch(
        propEq('role', 'admin'),
        renderNothing
    ),
    mapProps(omit(['role']))
);

export const withTopCompetitorsCount = withProps(
    () => {
        const { topCompetitorsCount: count } = usePortletConfig();

        if (count) {
            return {
                topCompetitorsCount: parseInt(count)
            };
        }
    }
);

export const withNetworkId = withProps(
    () => {
        const { displayPlayer, networkId, networkType } = useAppSelector(prop('leaderboard'));
        const playerNetworkData = useUserSelector(path(['userDetails', 'playerNetworkData']));
        const findNetworkData = data => data && find(propEq(networkType), data);

        switch (displayPlayer) {
            case DISPLAY_PLAYER.nicknameByNetworkType: {
                const networkData = findNetworkData(playerNetworkData);

                return networkData && {
                    networkId: networkData.networkId
                };
            }
            case DISPLAY_PLAYER.username: {
                return {
                    isToUseUsername: true
                };
            }
            case DISPLAY_PLAYER.nicknameByNetworkId:
                return {
                    networkId
                };
            default:
                break;
        }
    }
);

compose(
    onlyForPlayers,
    withLeaderBoardId,
    branch(
        ({ isNotLeaderBoardPage }) => isNotLeaderBoardPage,
        renderNothing
    ),
    branch(
        ({ leaderboardId }) => !leaderboardId,
        renderComponent(NavigateHome)
    ),
    withTopCompetitorsCount,
    withNetworkId,
    withLeaderBoardDetails,
    branch(
        ({ details, error }) => !details && !error,
        renderComponent(Skeleton)
    ),
    withEmptyState,
    withProps(
        ({ details }) => {
            const {
                leaderboardRules = [],
                topPrizes = [],
                commonPrize,
                status
            } = details;

            const {
                displayPrizes,
                displayCompetitorsTable: displayTable,
                competitorsTablePositioning,
                displayCountdown,
                setNicknamePage,
                showBackButton,
                backButtonUrl,
                useTournament
            } = usePortletConfig();

            return {
                competitorsTablePositioning,
                displayTable,
                displayCountdown: displayCountdown &&
                    status !== statuses.COMPLETED &&
                    status !== statuses.PENDING_REVIEW,
                displayGames: Boolean(leaderboardRules.length),
                displayPrizes: Boolean(topPrizes.length || commonPrize) && displayPrizes,
                setNicknamePage,
                showBackButton,
                backButtonUrl,
                useTournament
            };
        }
    )
)(Details);

export const MysteryParcelPage = compose(
    withPortlet({ forLoggedIn: true }),
    withNetworkId,
    Wrapped => (props) => {
        const Component = extractMysteryParcelId() === 'test'
            ? Wrapped
            : withForLoggedIn(Wrapped);
        return <Component {...props} />;
    },
    withDynamicReducer(),
    withTranslations,
    Wrapped => (props) => {
        const joined = useDynamicReducerSelector(state => state.join.joined);
        const displaySetNickname = useDynamicReducerSelector(state => state.join.displaySetNickname);
        return <Wrapped {...{ ...props, joined, displaySetNickname }} />;
    },
    withSetNicknameOnce,
    withProps(({ nicknameDetails, error: nicknameFetchError, isToUseUsername }) => {
        const hasNicknameFetchError = Boolean(nicknameFetchError);
        const hasNickname = Boolean(nicknameDetails && nicknameDetails.casinoNickname);
        const nicknameNeeded = !isToUseUsername;
        const dispatch = useDynamicReducerDispatch();
        const alreadyHasError = useDynamicReducerSelector(state => state.join.joinError);
        const joinError = hasNicknameFetchError || alreadyHasError;
        useEffect(() => {
            dispatch(updateJoinState({ joinError, hasNickname, nicknameNeeded }));
        }, [dispatch, hasNickname, joinError, nicknameNeeded]);
        return {};
    })
)(DetailedPage);

export default MysteryParcelPage;

componentService.registerPageComponents({ [MYSTERY_PARCEL_PAGE]: MysteryParcelPage });
